import { votingIds } from "@artists/challenges-testkit"
import { assertIsDefined, getAuthUrl } from "@artists/devkit"
import { Confirm } from "@artists/uikit"
import { Button } from "@mui/material"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { getENV } from "../../../../../@common"
import {
	CreateAccountCallToAction,
	createNewAccountAnalytics,
	useAnalytics,
} from "../../../../utils"

interface Props {
	slug: Maybe<string>
	isOpen: boolean
	onClose: () => void
}

export const UnregisteredUserPrompt = ({ isOpen, onClose, slug }: Props) => {
	const { t } = useTranslation("vote")
	const { send } = useAnalytics()

	const { title, primaryButton, text, secondaryButton, tertiaryButton } = t(
		"warnings.unregistered",
		{
			returnObjects: true,
		},
	)

	const onAuthClick = useCallback(
		() =>
			window.location.assign(
				getAuthUrl({
					host: getENV("SPOONFLOWER_HOST"),
					fallbackUrl: window.location.href,
				}),
			),
		[],
	)

	const onSignUpClick = useCallback(() => {
		assertIsDefined(slug)

		const analytics = createNewAccountAnalytics(
			CreateAccountCallToAction.VOTE_CLICK,
			slug,
		)
		send(analytics)

		window.location.assign(
			getAuthUrl({
				action: "register",
				host: getENV("SPOONFLOWER_HOST"),
				fallbackUrl: window.location.href,
			}),
		)
	}, [send, slug])

	return (
		<Confirm
			open={isOpen}
			onClose={onClose}
			title={title}
			subtitle={text}
			onConfirm={onClose}
			confirmText={primaryButton}
			actionWrapperProps={{
				flexWrap: { xs: "wrap", sm: "nowrap" },
				spacing: { xs: null, sm: 1 },
				alignSelf: { xs: "center", sm: "flex-end" },
				justifyContent: "center",
			}}
			actions={[
				<Button
					data-testid={votingIds.modalLogin}
					color='secondary'
					variant='contained'
					onClick={onAuthClick}
					sx={{
						textTransform: "none",
						flexGrow: { xs: 1, sm: "auto" },
						mr: { xs: 1, sm: 0 },
					}}
				>
					{primaryButton}
				</Button>,
				<Button
					data-testid={votingIds.modalSign}
					color='secondary'
					variant='contained'
					onClick={onSignUpClick}
					sx={{
						textTransform: "none",
						flexGrow: { xs: 1, sm: "auto" },
					}}
				>
					{secondaryButton}
				</Button>,
				<Button
					data-testid={votingIds.voteAsGuest}
					color='secondary'
					variant='outlined'
					onClick={onClose}
					sx={{
						textTransform: "none",
						marginTop: { xs: 1, sm: 0 },
						width: { xs: "100%", sm: "auto" },
					}}
				>
					{tertiaryButton}
				</Button>,
			]}
		/>
	)
}
